import { useState } from "react";
import { FileText, Video, MessageSquareQuote, ChevronRight, ArrowUpRight } from "lucide-react";

// Fictional, local-only examples. Never populated from candidate records.
const candidates = [
  {
    name: "Avery Morgan",
    initials: "AM",
    role: "Product leadership · B2B SaaS",
    resume:
      "Led a product squad of eight, taking a B2B onboarding platform from discovery to launch.",
    spotlight:
      "I like turning a complicated customer problem into a small, testable first release.",
    interview:
      "We agreed on one activation milestone, tested it with five customers, and used those findings to narrow the release.",
  },
  {
    name: "Jamie Chen",
    initials: "JC",
    role: "Platform strategy · Enterprise",
    resume: "Owned the roadmap for an internal developer platform across three engineering teams.",
    spotlight: "My work sits between engineering teams and the people depending on their tools.",
    interview: null,
  },
  {
    name: "Sam Taylor",
    initials: "ST",
    role: "Growth products · Marketplace",
    resume:
      "Ran onboarding experiments for a two-sided marketplace, partnering with design and analytics.",
    spotlight: null,
    interview:
      "We separated new sellers from returning sellers before deciding which onboarding experiment to run next.",
  },
];
const sources = [
  { label: "Resume", icon: FileText, key: "resume" },
  { label: "Spotlight", icon: Video, key: "spotlight" },
  { label: "Interview", icon: MessageSquareQuote, key: "interview" },
] as const;

export const EvidenceShortlistPreview = () => {
  const [selected, setSelected] = useState(0);
  const [source, setSource] = useState<(typeof sources)[number]["key"]>("resume");
  const candidate = candidates[selected];
  const passage = candidate[source];
  return (
    <figure className="recruit-preview">
      <div className="recruit-preview-top">
        <span className="recruit-eyebrow">Candidate review</span>
        <span className="recruit-example-label">Interactive example</span>
      </div>
      <div className="recruit-preview-title">
        <strong>Product Lead</strong>
        <span>3 profiles to explore</span>
      </div>
      <div className="recruit-candidate-list" aria-label="Example candidates">
        {candidates.map((item, index) => (
          <button
            key={item.name}
            onClick={() => setSelected(index)}
            aria-pressed={selected === index}
            className="recruit-candidate"
          >
            <span className="recruit-avatar" aria-hidden="true">
              {index === 0 ? (
                <img src="/images/marketing/avery-editorial.webp" alt="" width={32} height={32} />
              ) : (
                item.initials
              )}
            </span>
            <span>
              <strong>{item.name}</strong>
              <small>{item.role}</small>
            </span>
            <ChevronRight size={16} aria-hidden="true" />
          </button>
        ))}
      </div>
      <div className="recruit-evidence-pane">
        <div className="recruit-evidence-heading">
          <span className="recruit-eyebrow">Evidence for this requirement</span>
          <ArrowUpRight size={16} aria-hidden="true" />
        </div>
        <strong className="recruit-requirement">Taking a product from problem to delivery</strong>
        <div className="recruit-source-buttons" aria-label="Example evidence sources">
          {sources.map(({ key, label, icon: Icon }) => (
            <button key={key} aria-pressed={source === key} onClick={() => setSource(key)}>
              <Icon size={13} />
              {label}
            </button>
          ))}
        </div>
        <div className="recruit-source-content" aria-live="polite" aria-atomic="true">
          <div key={selected + source} className="recruit-passage">
            {passage ? (
              <blockquote>“{passage}”</blockquote>
            ) : (
              <p className="recruit-missing">
                No {source === "spotlight" ? "published Spotlight Intro" : "completed interview"} in
                this example. Keep reviewing the available evidence.
              </p>
            )}
            <span className="recruit-source-note">
              {candidate.name} · {sources.find((item) => item.key === source)?.label}
              {passage ? " excerpt" : " unavailable"}
            </span>
          </div>
        </div>
      </div>
      <figcaption>
        Fictional profiles and excerpts. A simplified view of the workflow—not a live ranking.
      </figcaption>
    </figure>
  );
};
